import _ from "lodash";
import { createSelector } from "reselect";
import { GLOBAL_PROJECT_TYPE_CHAMBER } from "./types";

/* Resolved lazily: reducers/index.js -> stone-bed-reducer -> this module, so a
   top-level store import leaves that cycle half-initialised depending on entry point. */
const getStore = () => require("../store").default;

export default class Calculations{
  /**
   * @param {Array<ChamberRow>} chamberRows
   * @param {number} chamberRowSpacingPixels
   * @param {number} sepDistancePixels
   * @param underDrainSepDistancePixels
   * @param includeUnderdrain
   * @param manifoldSeparations
   * @param manifolds
   * @param newManifoldSeparations
   * @param scalePixels
   * @param scaleValue
   * @param sepDistance
   * @param chamberRowSpacing
   * @returns {Array<number}
   */
  static computeUpperPoints(
    chamberRows,
    chamberRowSpacingPixels,
    sepDistancePixels,
    underDrainSepDistancePixels,
    includeUnderdrain,
    manifoldSeparations,
    manifolds,
    newManifoldSeparations,
    scalePixels,
    scaleValue,
    sepDistance,
    chamberRowSpacing ) {
    /* the upper row */
    let row;
    let rawPoints = [];
    let rawPointDoubleLinkedList = [];
    let points;
    let coordinates;
    let rowSpacing;
    let halfChamberRowSpacingPixels = chamberRowSpacingPixels / 2;
    let firstActiveRow, lastActiveRow;

    /* the active rows coordinates */
    let rowsCoordinates = [];

    /* first find the average X(upper distance of rows on top of bed) */
    for (let i = chamberRows.length - 1; i >= 0; i--) {
      if (chamberRows[i].getActiveCount()) {
        /* get row reference */
        row = chamberRows[i];
        /* get this row bound coordinates */
        coordinates = row.getBoundsCoordinatesWithManifolds(
          chamberRows,
          manifoldSeparations,
          manifolds,
          true,
          newManifoldSeparations,
          scalePixels,
          scaleValue,
          sepDistance,
          chamberRowSpacing);
        /* push the coordinates */
        rowsCoordinates[i] = coordinates;
        /* set first active row */
        if (!lastActiveRow) {
          lastActiveRow = i;
        }
        /* set last active row */
        firstActiveRow = i;
      } else {
        /* push the null value */
        rowsCoordinates[i] = null;
      }
    }

    /* find the upper row with more than one active chamber */
    for (let i = chamberRows.length - 1; i >= 0; i--) {
      /* get the current coordinates */
      coordinates = rowsCoordinates[i];
      /* if coordinates are present */
      if (coordinates) {
        /* compute row spacing */
        rowSpacing =
          chamberRowSpacingPixels * ( i - 1 ) + chamberRowSpacingPixels / 2;
        /* add coordinates to points */
        /* calculate X coordinate from top left of the upper chamber */
        let topLeftX, topRightX;
        topLeftX = coordinates.topLeft.x - sepDistancePixels;
        topRightX = coordinates.topRight.x - sepDistancePixels;

        /* the previous and next rows */
        let prevR = rowsCoordinates[i - 1];
        let nextR = rowsCoordinates[i + 1];
        let leftOffsetY = 0;
        let rightOffsetY = 0;
        /* if neighbor rows are higher or lower, add separation offset */
        if (prevR && prevR.topLeft.x < coordinates.topRight.x) {
          rightOffsetY = sepDistancePixels - halfChamberRowSpacingPixels;
        } else if (prevR && prevR.topLeft.x > coordinates.topRight.x) {
          rightOffsetY = -( sepDistancePixels - halfChamberRowSpacingPixels );
        }

        if (nextR && nextR.topRight.x < coordinates.topLeft.x) {
          leftOffsetY = -( sepDistancePixels - halfChamberRowSpacingPixels );
        } else if (nextR && nextR.topRight.x > coordinates.topLeft.x) {
          leftOffsetY = sepDistancePixels - halfChamberRowSpacingPixels;
        }

        let manifoldTrunkOffset = coordinates.hasManifold
          ? coordinates.manifoldSeparation - sepDistancePixels
          : 0;

        let manifoldLeftBendOffset = coordinates.hasLeftBendSeparation
          ? coordinates.leftManifoldBendSeparation
          : 0;

        let manifoldRightBendOffset = coordinates.hasRightBendSeparation
          ? coordinates.rightManifoldBendSeparation
          : 0;

        let manifoldLeftEndCapOffset = coordinates.hasLeftEndCapSeparation
          ? coordinates.leftManifoldEndCapSeparation
          : 0;

        let manifoldRightEndCapOffset = coordinates.hasRightEndCapSeparation
          ? coordinates.rightManifoldEndCapSeparation
          : 0;

        /* set the points */
        const leftRawPoint = [
          topLeftX - manifoldTrunkOffset,
          coordinates.topLeft.y + rowSpacing + chamberRowSpacingPixels + leftOffsetY
        ];
        const rightRawPoint = [
          topRightX - manifoldTrunkOffset,
          coordinates.topRight.y + rowSpacing + rightOffsetY
        ];

        rawPoints.push(leftRawPoint);
        rawPoints.push(rightRawPoint);

        const pointIndex = rawPoints.length;

        rawPointDoubleLinkedList.push({
          leftPointIndex: pointIndex - 2,
          rightPointIndex: pointIndex - 1,
          manifoldLeftEndCapOffset,
          manifoldRightEndCapOffset,
          manifoldLeftBendOffset,
          manifoldRightBendOffset
        });
      }
    }

    rawPoints = Calculations.shiftRawPoints(rawPoints, rawPointDoubleLinkedList, true);  // Shift the points to side
    points = Calculations.getPrecisePoints(rawPoints);  // Get the necessary points

    let
      endCapLeftSeparation = null,
      endCapRightSeparation = null;

    try {
      const { endCapSeparation } = manifoldSeparations;
      if (endCapSeparation) {
        endCapLeftSeparation = points[1] + endCapSeparation;
        endCapRightSeparation = points[points.length - 1] - endCapSeparation;
      }
    } catch (exception) {
    }

    /* adding offset to last and first point */
    const chamberBedSeparation = sepDistancePixels - halfChamberRowSpacingPixels;
    points[1] += chamberBedSeparation;
    points[points.length - 1] -= chamberBedSeparation;

    /* check if underdrain needs offset */
    if (firstActiveRow >= 0 && includeUnderdrain) {
      let firstRowHasOutlet = chamberRows[firstActiveRow].hasCornerOutlet()
        || chamberRows[firstActiveRow].hasInternalOutlet();
      let lastRowHasOutlet = chamberRows[lastActiveRow].hasCornerOutlet()
        || chamberRows[lastActiveRow].hasCornerOutlet();
      /* offset the polyline by that distance */
      if (firstRowHasOutlet) {
        points[points.length - 1] -= underDrainSepDistancePixels;
      }
      if (lastRowHasOutlet && firstActiveRow !== lastActiveRow) {
        points[1] += underDrainSepDistancePixels;
      }
    }

    // Handle horizontal separations
    if (endCapLeftSeparation && rawPointDoubleLinkedList.some(item => item.leftPointIndex === 0 && item.manifoldLeftEndCapOffset)) {
      points[1] = Math.max(points[1], endCapLeftSeparation);
    }

    if (endCapRightSeparation && rawPointDoubleLinkedList.some(item => item.rightPointIndex === rawPoints.length - 1 && item.manifoldRightEndCapOffset)) {
      points[points.length - 1] = Math.min(points[1], endCapRightSeparation);
    }

    const _points = Calculations._fixInterceptions(points);

    /* return the points */
    return _points;
  }

  /**
   * @param {Array<ChamberRow>} chamberRows
   * @param {number} chamberRowSpacingPixels
   * @param {number} sepDistancePixels
   * @param underDrainSepDistancePixels
   * @param includeUnderdrain
   * @param manifoldSeparations
   * @param manifolds
   * @param newManifoldSeparations
   * @param scalePixels
   * @param scaleValue
   * @param sepDistance
   * @param chamberRowSpacing
   * @returns {Array<number}
   */
  static computeLowerPoints(
    chamberRows,
    chamberRowSpacingPixels,
    sepDistancePixels,
    underDrainSepDistancePixels,
    includeUnderdrain,
    manifoldSeparations,
    manifolds,
    newManifoldSeparations,
    scalePixels,
    scaleValue,
    sepDistance,
    chamberRowSpacing ) {
    /* the upper row */
    let row;
    let rawPoints = [];
    let rawPointDoubleLinkedList = [];
    let points;
    let coordinates;
    let rowSpacing;
    let halfChamberRowSpacingPixels = chamberRowSpacingPixels / 2;
    let firstActiveRow, lastActiveRow;

    /* the active rows coordinates */
    let rowsCoordinates = [];

    /* first find the average X(upper distance of rows on top of bed) */
    for (let i = chamberRows.length - 1; i >= 0; i--) {
      if (chamberRows[i].getActiveCount()) {
        /* get row reference */
        row = chamberRows[i];
        /* get this row bound coordinates */

        coordinates = row.getBoundsCoordinatesWithManifolds(
          chamberRows,
          manifoldSeparations,
          manifolds,
          false,
          newManifoldSeparations,
          scalePixels,
          scaleValue,
          sepDistance,
          chamberRowSpacing);
        /* push the coordinates */
        rowsCoordinates[i] = coordinates;
        /* set first active row */
        if (!lastActiveRow) {
          lastActiveRow = i;
        }
        /* set last active row */
        firstActiveRow = i;
      } else {
        /* push the null value */
        rowsCoordinates[i] = null;
      }
    }

    /* find the upper row with more than one active chamber */
    for (let i = 0; i < chamberRows.length; i++) {
      /* get the current coordinates */
      coordinates = rowsCoordinates[i];
      /* if coordinates are present */
      if (coordinates) {
        /* compute row spacing */
        rowSpacing =
          chamberRowSpacingPixels * ( i - 1 ) + halfChamberRowSpacingPixels;

        /* the previous and next rows */
        let prevR = rowsCoordinates[i - 1];
        let nextR = rowsCoordinates[i + 1];
        let leftOffsetY = 0;
        let rightOffsetY = 0;
        /* if neighbor rows are higher or lower, add separation offset */
        if (prevR && prevR.downRight.x < coordinates.downLeft.x) {
          rightOffsetY = -( sepDistancePixels - halfChamberRowSpacingPixels );
        } else if (prevR && prevR.downRight.x > coordinates.downLeft.x) {
          rightOffsetY = sepDistancePixels - halfChamberRowSpacingPixels;
        }

        if (nextR && nextR.downRight.x < coordinates.downLeft.x) {
          leftOffsetY = sepDistancePixels - halfChamberRowSpacingPixels;
        } else if (nextR && nextR.downRight.x > coordinates.downLeft.x) {
          leftOffsetY = -( sepDistancePixels - halfChamberRowSpacingPixels );
        }

        /* calculate the x coordinates */
        let downLeftX, downRightX;
        downRightX = coordinates.downRight.x + sepDistancePixels;
        downLeftX = coordinates.downLeft.x + sepDistancePixels;

        let manifoldLeftBendOffset = coordinates.hasLeftBendSeparation
          ? coordinates.leftManifoldBendSeparation
          : 0;

        let manifoldRightBendOffset = coordinates.hasRightBendSeparation
          ? coordinates.rightManifoldBendSeparation
          : 0;

        /* check if has lower manifold */
        let manifoldTrunkOffset = coordinates.hasManifold
          ? coordinates.manifoldSeparation - sepDistancePixels
          : 0;

        let manifoldLeftEndCapOffset = coordinates.hasLeftEndCapSeparation
          ? coordinates.leftManifoldEndCapSeparation
          : 0;

        let manifoldRightEndCapOffset = coordinates.hasRightEndCapSeparation
          ? coordinates.rightManifoldEndCapSeparation
          : 0;

        /* set the points */
        const rightRawPoint = [
          downRightX + manifoldTrunkOffset,
          coordinates.downRight.y + rowSpacing + rightOffsetY
        ];
        const leftRawPoint = [
          downLeftX + manifoldTrunkOffset,
          coordinates.downLeft.y + rowSpacing + chamberRowSpacingPixels + leftOffsetY
        ];

        rawPoints.push(rightRawPoint);
        rawPoints.push(leftRawPoint);

        const pointIndex = rawPoints.length;

        rawPointDoubleLinkedList.push({
          leftPointIndex: pointIndex - 2,
          rightPointIndex: pointIndex - 1,
          manifoldLeftEndCapOffset: manifoldLeftEndCapOffset,
          manifoldRightEndCapOffset: manifoldRightEndCapOffset,
          manifoldLeftBendOffset: manifoldLeftBendOffset,
          manifoldRightBendOffset: manifoldRightBendOffset
        });
      }
    }

    rawPoints = Calculations.shiftRawPoints(rawPoints, rawPointDoubleLinkedList, false);  // Shift the points to side
    points = Calculations.getPrecisePoints(rawPoints);  // Get the necessary points

    let
      endCapLeftSeparation = null,
      endCapRightSeparation = null;

    try {
      const { endCapSeparation } = manifoldSeparations;
      if (endCapSeparation) {
        endCapRightSeparation = points[1] - endCapSeparation;
        endCapLeftSeparation = points[points.length - 1] + endCapSeparation;
      }
    } catch (exception) {
    }

    /* adding offset to last and first point */
    const chamberBedSeparation = sepDistancePixels - halfChamberRowSpacingPixels;
    points[1] -= chamberBedSeparation;
    points[points.length - 1] += chamberBedSeparation;

    /* check if underdrain needs offset */
    if (firstActiveRow >= 0 && includeUnderdrain) {
      let firstRowHasOutlet = chamberRows[firstActiveRow].hasCornerOutlet()
        || chamberRows[firstActiveRow].hasInternalOutlet();
      let lastRowHasOutlet = chamberRows[lastActiveRow].hasCornerOutlet()
        || chamberRows[lastActiveRow].hasInternalOutlet();
      /* offset the polyline by that distance */
      if (lastRowHasOutlet && firstActiveRow != lastActiveRow) {
        points[points.length - 1] += underDrainSepDistancePixels;
      }
      if (firstRowHasOutlet) {
        points[1] -= underDrainSepDistancePixels;
      }
    }

    // Handle horizontal separations
    if (endCapRightSeparation && rawPointDoubleLinkedList.some(item => item.leftPointIndex === 0 && item.manifoldRightEndCapOffset)) {
      points[1] = Math.min(points[1], endCapRightSeparation);
    }

    if (endCapLeftSeparation && rawPointDoubleLinkedList.some(item => item.rightPointIndex === rawPoints.length - 1 && item.manifoldLeftEndCapOffset)) {
      points[points.length - 1] = Math.max(points[1], endCapLeftSeparation);
    }

    const _points = Calculations._fixInterceptions(points);

    /* return the points */
    return _points;
  }

  /**
   * Shift the raw points to the left or righ with end caps
   *
   * @param rawPoints
   * @param rawPointDoubleLinkedList
   * @param isUpper
   * @returns {*}
   */
  static shiftRawPoints(
    rawPoints,
    rawPointDoubleLinkedList,
    isUpper
  ) {
    try {
      let _rawPoints = [...rawPoints];

      // shift end caps after the fact
      rawPointDoubleLinkedList.forEach(rawPointItem => {
        const {
          leftPointIndex,
          rightPointIndex,
          manifoldLeftEndCapOffset,
          manifoldRightEndCapOffset,
          manifoldLeftBendOffset,
          manifoldRightBendOffset,
          immediateLeftPoint,
          immediateRightPoint
        } =
          isUpper ?
            Calculations._shiftRawPointsIsUpper(rawPointItem, _rawPoints) :
            Calculations._shiftRawPointsIsLower(rawPointItem, _rawPoints);

        if (
          ( manifoldLeftEndCapOffset + manifoldRightEndCapOffset === 0 ) &&
          ( manifoldLeftBendOffset + manifoldRightBendOffset === 0 )) {
          return;
        }

        if (( manifoldLeftEndCapOffset || manifoldLeftBendOffset ) && immediateLeftPoint !== null) {
          _rawPoints[leftPointIndex][1] += manifoldLeftEndCapOffset - manifoldLeftBendOffset;
          immediateLeftPoint[1] += manifoldLeftEndCapOffset - manifoldLeftBendOffset;
        }

        //if (manifoldRightBendOffset && immediateRightPoint !== null) {
        //  _rawPoints[rightPointIndex][1] -= manifoldRightBendOffset;
        //  immediateRightPoint[1] -= manifoldRightBendOffset;
        //}


        if (( manifoldRightEndCapOffset || manifoldRightBendOffset ) && immediateRightPoint !== null) {
          _rawPoints[rightPointIndex][1] -= manifoldRightEndCapOffset - manifoldRightBendOffset;
          immediateRightPoint[1] -= manifoldRightEndCapOffset - manifoldRightBendOffset;
        }
      });

      return _rawPoints;
    } catch (exception) {
      console.warn("An issue occurred while shifting raw points", exception);
    }

    return rawPoints;
  }

  static _shiftRawPointsIsUpper( rawPointItem, rawPoints ) {
    const {
      leftPointIndex,
      rightPointIndex,
      manifoldLeftEndCapOffset,
      manifoldRightEndCapOffset,
      manifoldLeftBendOffset,
      manifoldRightBendOffset
    } = rawPointItem;

    const immediateLeftPoint = leftPointIndex > 0 ? rawPoints[leftPointIndex - 1] : null;
    const immediateRightPoint = rightPointIndex < rawPoints.length - 1 ? rawPoints[rightPointIndex + 1] : null;

    return {
      leftPointIndex,
      rightPointIndex,
      manifoldLeftEndCapOffset,
      manifoldRightEndCapOffset,
      manifoldLeftBendOffset,
      manifoldRightBendOffset,
      immediateLeftPoint,
      immediateRightPoint
    };
  }

  static _shiftRawPointsIsLower( rawPointItem, rawPoints ) {
    const {
      leftPointIndex: rightPointIndex,
      rightPointIndex: leftPointIndex,
      manifoldLeftEndCapOffset,
      manifoldRightEndCapOffset,
      manifoldLeftBendOffset,
      manifoldRightBendOffset
    } = rawPointItem;

    const immediateLeftPoint = leftPointIndex < rawPoints.length - 1 ? rawPoints[leftPointIndex + 1] : null;
    const immediateRightPoint = rightPointIndex > 0 ? rawPoints[rightPointIndex - 1] : null;

    return {
      leftPointIndex,
      rightPointIndex,
      manifoldLeftEndCapOffset,
      manifoldRightEndCapOffset,
      manifoldLeftBendOffset,
      manifoldRightBendOffset,
      immediateLeftPoint,
      immediateRightPoint
    };
  }

  static _fixInterceptions( points ) {
    points = points.map(point => Number.parseFloat(Number.parseFloat(point).toFixed(8)));  // Some values are precise, some are not

    const pointPairs = _.chunk(points, 2);
    const lines = [];

    pointPairs.reduce(( lastPointPair, currentPointPair ) => {
      if (lastPointPair !== null) {
        lines.push([lastPointPair, currentPointPair]);
      }

      return currentPointPair;
    }, null);

    for (let i = 2; i < lines.length; ++i) {
      const line = lines[i];
      const lefthandLines = lines.slice(0, i - 1);  // Skip line to the left of the current line

      for (let j = 0; j < lefthandLines.length; ++j) {
        const lefthandLine = lefthandLines[j];

        if (lefthandLine === null) {
          continue;
        }

        const intersectionPoint = Calculations._getInterceptingPoint(lefthandLine[0], lefthandLine[1], line[0], line[1]);

        if (intersectionPoint) {
          lines[i][0] = intersectionPoint;
          lines[j][1] = intersectionPoint;

          for (let k = j + 1; k < i; ++k) {
            lines[k] = null;
          }

          break;
        }
      }
    }

    return _.flatten(_.uniqWith(_.flatten(lines.filter(Boolean)), _.isEqual));
  }

  static _getInterceptingPoint( a, b, c, d ) {
    const [aX, aY] = a;
    const [bX, bY] = b;
    const [cX, cY] = c;
    const [dX, dY] = d;

    const { slope: m1, yIntercept: b1 } = this._getLinearEquation(a, b);
    const { slope: m2, yIntercept: b2 } = this._getLinearEquation(c, d);

    if (m1 === m2) {  // Parallel lines
      return null;
    }

    let x, y;

    if (m1 === undefined) {  // Vertical line
      y = aY;

      if (m2 === 0) {
        x = cX;
      } else {
        x = ( y - b2 ) / m2;
      }
    } else if (m2 === undefined) {  // Vertical line
      y = cY;

      if (m1 === 0) {
        x = aX;
      } else {
        x = ( y - b1 ) / m1;
      }
    } else {  // Guaranteed the two slopes aren't equal and not at 90 degree angles
      const denominator = m1 - m2;
      const numerator = b2 - b1;

      x = numerator / denominator;
      y = ( m1 * x ) + b1;
    }

    // Is the intersection within the two lines?
    const
      _sort = ( a, b ) => a - b,
      x1Set = [aX, bX],
      y1Set = [aY, bY],
      x2Set = [cX, dX],
      y2Set = [cY, dY];

    x1Set.sort(_sort);
    y1Set.sort(_sort);
    x2Set.sort(_sort);
    y2Set.sort(_sort);

    const
      [x1Min, x1Max] = x1Set,
      [y1Min, y1Max] = y1Set,
      [x2Min, x2Max] = x2Set,
      [y2Min, y2Max] = y2Set;

    const inFirstLine = ( x >= x1Min && x <= x1Max ) && ( y >= y1Min && y <= y1Max );
    const inSecondLine = ( x >= x2Min && x <= x2Max ) && ( y >= y2Min && y <= y2Max );

    if (inFirstLine && inSecondLine) {
      return [x, y];
    }

    return null;
  }

  static _getLinearEquation( a, b ) {
    // y = mx + b
    const [aX, aY] = a;
    const [bX, bY] = b;

    const denominator = ( bX - aX );
    const numerator = ( bY - aY );

    let
      slope,
      yIntercept;

    if (numerator === 0) {
      // x = aX
      slope = undefined;
      yIntercept = undefined;
    } else if (denominator === 0) {
      // y = aY
      slope = 0;
      yIntercept = aY;
    } else {
      // y = mx + b
      slope = numerator / denominator;
      yIntercept = aY - ( slope * aX );
    }

    return {
      slope,
      yIntercept
    };
  }

  /**
   * Remove points along a line (horizontal or vertical) to get a cleaner set of points
   *
   * @param rawPoints
   * @returns {Array}
   */
  static getPrecisePoints( rawPoints ) {
    const points = [];
    const pointPairs = _.uniqWith(rawPoints, _.isEqual);

    const setPoints = ( data, maximumPair, minimumPair, pointPair, direction, newDirection ) => {
      // Test the new direction and apply the points if needed
      try {
        if (pointPair[newDirection] === minimumPair[newDirection]) {
          if (direction === null || direction === newDirection) {  // Minimum would already be applied
            return { ...data, direction: newDirection, maximumPair: pointPair };
          }

          // Changed direction; push the last (maximum) and current pair
          return resetPoint(maximumPair, pointPair, newDirection);
        }
      } catch (exception) {
        console.warn(exception);
      }

      return null;
    };

    const resetPoint = ( maximumPair, pointPair, newDirection ) => {
      points.push(maximumPair.x, maximumPair.y);
      points.push(pointPair.x, pointPair.y);

      return { direction: newDirection, minimumPair: pointPair, maximumPair: pointPair };
    };

    try {
      const { maximumPair } = pointPairs.reduce(( data, pointPair, index ) => {
        const _pointPair = { x: pointPair[0], y: pointPair[1] };
        const { direction, minimumPair, maximumPair } = data;
        const _setPoints = setPoints.bind(null, data, maximumPair, minimumPair, _pointPair, direction);
        const _resetPoint = resetPoint.bind(null, maximumPair, _pointPair);
        let reduction;

        if (index === 0) {  // Just starting
          points.push(_pointPair.x, _pointPair.y);
          return { direction: null, minimumPair: _pointPair, maximumPair: _pointPair };
        }

        reduction = _setPoints("x");
        if (reduction instanceof Object) {
          return reduction;
        }

        reduction = _setPoints("y");
        if (reduction instanceof Object) {
          return reduction;
        }

        // Neither dimension was used!!
        return _resetPoint(null);
      }, { direction: null, minimumPair: null, maximumPair: null });

      const shouldSetMaximumPair = maximumPair instanceof Object &&
        !( maximumPair.x === points[points.length - 2] && maximumPair.y === points[points.length - 1] );

      if (shouldSetMaximumPair) {
        points.push(maximumPair.x, maximumPair.y);
      }
    } catch (exception) {
      console.warn("An issue occurred while getting precise points", exception);
    }

    return points;
  }

  /**
   * This function computes stone bed points from
   * @param {Object} state
   * @param {Array<ChamberRow>} chamberRows
   * @param {Array<ChamberRow>} chamberRows
   * @param {number} chamberRowSpacingPixels
   * @param {number} sepDistancePixels
   * @param {number} manifoldSepDistancePixels
   * @returns {Array<number}
   */
  static computePoints(
    state,
    chamberRows,
    chamberRowSpacingPixels,
    sepDistancePixels,
    underDrainSepDistancePixels,
    includeUnderdrain,
    manifoldSeparations,
    upperManifolds,
    lowerManifolds,
    sepDistance,
    chamberType,
    chamberRowSpacing,
    scalePixels,
    scaleValue
  ) {
    /* if chamber rows have not been deserialized yet */
    if (chamberRows.length && !chamberRows[0].getActiveCount) {
      return [];
    }

    let newUpperManifoldSeparations = [];

    /* the bed points */
    let upperPoints = Calculations.computeUpperPoints(
      chamberRows,
      chamberRowSpacingPixels,
      sepDistancePixels,
      underDrainSepDistancePixels,
      includeUnderdrain,
      manifoldSeparations,
      upperManifolds,
      newUpperManifoldSeparations,
      scalePixels,
      scaleValue,
      sepDistance,
      chamberRowSpacing
    );

    let newLowerManifoldSeparations = [];

    let lowerPoints = Calculations.computeLowerPoints(
      chamberRows,
      chamberRowSpacingPixels,
      sepDistancePixels,
      underDrainSepDistancePixels,
      includeUnderdrain,
      manifoldSeparations,
      lowerManifolds,
      newLowerManifoldSeparations,
      scalePixels,
      scaleValue,
      sepDistance,
      chamberRowSpacing
    );

    // Do one last thing: make sure that the Y value for all four corners are in line!!
    if (upperPoints.length >= 4 && lowerPoints.length >= 4) {
      const upperLeftY = upperPoints[1];
      const upperRightY = upperPoints[upperPoints.length - 1];
      const lowerLeftY = lowerPoints[lowerPoints.length - 1];
      const lowerRightY = lowerPoints[1];

      const maxLeftY = Math.max(upperLeftY, lowerLeftY);
      const minRightY = Math.min(upperRightY, lowerRightY);

      upperPoints[1] = maxLeftY;
      lowerPoints[lowerPoints.length - 1] = maxLeftY;

      upperPoints[upperPoints.length - 1] = minRightY;
      lowerPoints[1] = minRightY;
    }

    /* the final points */
    let points = [...lowerPoints, ...upperPoints, ...lowerPoints.slice(0, 2)]
      .filter(n => !isNaN(n));

    /* return points and remove not numbers */
    return points;
  }

  static calculateAngle( x1, y1, x2, y2 ) {
    return parseInt(
      Math.abs(
        ( Math.atan2(Math.abs(y2 - y1), Math.abs(x2 - x1)) * 180 ) / Math.PI
      ),
      10
    );
  }

  /* computing angles for each set of points */
  static computeAngles( pts ) {
    /* the original neighbor angles for each point */
    let angles = [];

    for (let i = 0; i < pts.length; i += 2) {
      let index = parseInt(i / 2, 10);
      /* the index of previous and next points */
      let prevIdx = i === 0 ? pts.length - 2 : i - 2;
      let nextIdx = ( i + 2 ) % pts.length;

      /* the previous angle */
      let prevAngl = Calculations.calculateAngle(
        pts[prevIdx],
        pts[prevIdx + 1],
        pts[i],
        pts[i + 1]
      );
      let nextAngl = Calculations.calculateAngle(
        pts[nextIdx],
        pts[nextIdx + 1],
        pts[i],
        pts[i + 1]
      );

      /* validate that angles are straight angles */
      if (
        ( prevAngl !== 0 && prevAngl !== 90 ) ||
        ( nextAngl !== 0 && nextAngl !== 90 )
      ) {
        console.info(
          `Warming calculating angles, not compliant with 90 or 0 degrees, prevAngl: ${prevAngl}, nextAngl: ${nextAngl}`
        );
      }
      /* the angles definition */
      let angl = {
        prev: prevAngl,
        next: nextAngl
      };
      /* set the angle to this point index */
      angles[index] = angl;
    }

    /* return the new angle */
    return angles;
  }
}

export const getScaledValue = ( scalePixels, scaleValue, value ) => scalePixels * ( value / scaleValue );

export const getUnscaledValue = ( scalePixels, scaleValue, value ) => value / ( scalePixels / scaleValue );

export const getScalePixelsByState = state => state.globals.projectType === GLOBAL_PROJECT_TYPE_CHAMBER
  ? state.globals.scalePixels
  : state.pipeGlobals.scalePixels;

export const getScaleValueByState = state => state.globals.projectType === GLOBAL_PROJECT_TYPE_CHAMBER
  ? state.globals.scaleValue
  : state.pipeGlobals.scaleValue;

export const getPlanScaleInchesByState = state => state.globals.projectType === GLOBAL_PROJECT_TYPE_CHAMBER
  ? state.globals.planScaleInches
  : state.pipeGlobals.planScaleInches;

export const getPlanScaleMtsByState = state => state.globals.projectType === GLOBAL_PROJECT_TYPE_CHAMBER
  ? state.globals.planScaleMts
  : state.pipeGlobals.planScaleMts;


export const getScaledPixels = value => {
  const getPixels = createSelector(
    getScalePixelsByState,
    getScaleValueByState,
    ( scalePixels, scaleValue ) => getScaledValue(scalePixels, scaleValue, value)
  );
  return getPixels(getStore().getState());
};

export const getUnscaledFeet = value => {
  const getFeet = createSelector(
    getScalePixelsByState,
    getScaleValueByState,
    ( scalePixels, scaleValue ) => getUnscaledValue(scalePixels, scaleValue, value)
  );

  return getFeet(getStore().getState());
};



// WEBPACK FOOTER //
// ./src/lib/calculations.js